import { registerPlugin } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';

export interface PipPlugin {
  setPipEnabled(options: { enabled: boolean }): Promise<void>;
  setVideoPlaying(options: {
    playing: boolean;
    /** PiP aspect numerator (default 16). Use 9 for vertical Shorts/Reels. */
    width?: number;
    /** PiP aspect denominator (default 9). Use 16 for vertical Shorts/Reels. */
    height?: number;
  }): Promise<void>;
  enterPip(): Promise<{ entered: boolean }>;
  isPipSupported(): Promise<{ supported: boolean }>;
  /** Align native status/nav bars with web dark mode. */
  setChromeDark(options: { dark: boolean }): Promise<void>;
  
  addListener(
    eventName: 'pipModeChanged',
    listenerFunc: (info: { isPip: boolean }) => void
  ): Promise<import('@capacitor/core').PluginListenerHandle>;
  
  removeAllListeners(): Promise<void>;
}

const Pip = registerPlugin<PipPlugin>('Pip');

/** Default PiP preference when the user has never toggled it (native only). */
export function getPipPreference(): boolean {
  const stored = localStorage.getItem('pip_enabled');
  if (stored === null) return true;
  return stored === 'true';
}

export function setPipPreference(enabled: boolean): void {
  localStorage.setItem('pip_enabled', String(enabled));
  if (Capacitor.isNativePlatform()) {
    Pip.setPipEnabled({ enabled }).catch(console.error);
  }
}

/**
 * Arm / disarm system auto-enter PiP while an embed is open.
 * Pass aspect for vertical platforms (instagram / youtube shorts → 9:16).
 */
export function notifyVideoPlaying(
  playing: boolean,
  aspect?: { width: number; height: number }
): void {
  if (!Capacitor.isNativePlatform()) return;
  Pip.setVideoPlaying({
    playing,
    width: aspect?.width ?? 16,
    height: aspect?.height ?? 9,
  }).catch(console.error);
}

/** Prefer 9:16 for vertical-first platforms; 16:9 otherwise. */
export function pipAspectForPlatform(platform?: string | null): {
  width: number;
  height: number;
} {
  const p = (platform || '').toLowerCase();
  if (p === 'instagram') {
    return { width: 9, height: 16 };
  }
  // Twitter/X videos vary; landscape default — TwitterEmbed overrides with real media aspect
  if (p === 'twitter' || p === 'x') {
    return { width: 16, height: 9 };
  }
  // YouTube can be either; default landscape (Shorts still ok with mild letterbox)
  return { width: 16, height: 9 };
}

/** Keep Android system bars in sync with the in-app theme toggle. */
export function notifyChromeDark(dark: boolean): void {
  if (!Capacitor.isNativePlatform()) return;
  Pip.setChromeDark({ dark }).catch(console.error);
}

export default Pip;
